import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { enumMarineInsuranceStatusType } from '@app/data-layer/enums/marine-insurance/enumInsuranceStatusType.enum';
import { MarineInsuranceModel, MarineInsuranceSearchShipmentModel } from '@app/data-layer/models/marine-insurance';
import { DocumentsRepository } from '@app/data-layer/repositories';
import { DocumentModel } from '@data-layer/models/documents';
import { MarineInsuranceDocumentsRepository } from '@app/data-layer/repositories/marine-insurance';
import { enumCompanyPlacement } from '@enum/enumCompanyPlacement';
import { AlertService } from '@services/alert.service';
import { MarineInsuranceFacade } from '@shared/marine-insurance-shared/services';
import { ReplaySubject } from 'rxjs';
import { finalize, switchMap, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-marine-insurance-indicator',
  templateUrl: './marine-insurance-indicator.component.html',
  styleUrls: ['./marine-insurance-indicator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarineInsuranceIndicatorComponent implements OnDestroy {
  @Input() public entityReferenceNumber: string;
  @Input() public entityRedirectPath: string;
  @Input() public companyPlacement: enumCompanyPlacement;
  @Input() public entityStatus: enumMarineInsuranceStatusType;
  /** Borderless white variant for the dark status bars on the ISF/ES details pages. */
  @Input() public isPlain: boolean = false;
  /**
   * Set on the entity's own page: navigating there again only changes a query param, so Angular
   * reuses the component and the `openMarineInsuranceQuoteForm` snapshot read never re-runs.
   * The host opens the quote sidebar itself instead.
   */
  @Input() public isOnEntityPage: boolean = false;
  /** Greyed-out and inert shield; `disabledTooltipKey` tells the user why insurance can no longer be bought. */
  @Input() public isDisabled: boolean = false;
  @Input() public disabledTooltipKey: string = 'LABELS.NOT_AVAILABLE';

  @Output() public addInsurance: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('insuranceCertificateLink', { static: false }) public insuranceCertificateLinkRef: ElementRef;

  public isDownloading: boolean = false;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef,
    private alertService: AlertService,
    private documentsRepository: DocumentsRepository,
    private marineInsuranceFacade: MarineInsuranceFacade,
    private marineInsuranceDocumentsRepository: MarineInsuranceDocumentsRepository,
  ) {}

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Only a policy bought through EezyImport counts as insured — every other status is an upsell opportunity. */
  public get isInsured(): boolean {
    return this.entityStatus === enumMarineInsuranceStatusType.INSURED;
  }

  public get insuranceTooltipTranslationText(): string {
    if (this.isDisabled) {
      return this.disabledTooltipKey;
    }

    return this.isInsured ? 'LABELS.INSURED' : 'LABELS.GET_INSURANCE';
  }

  public get iconName(): string {
    return this.isInsured ? 'icon_insured_active.svg' : 'icon_insured_inactive.svg';
  }

  public onIndicatorClick(): void {
    if (this.isDisabled || this.isDownloading) {
      return;
    }

    if (this.isInsured) {
      this.downloadEntityInsuranceCertificate(this.entityReferenceNumber);
    } else if (this.isOnEntityPage) {
      this.addInsurance.emit();
    } else {
      this.redirectToEntityInsurance(this.entityReferenceNumber);
    }
  }

  private redirectToEntityInsurance(referenceNumber: string): void {
    this.router.navigate([this.entityRedirectPath, referenceNumber], { queryParams: { openMarineInsuranceQuoteForm: true } });
  }

  private downloadEntityInsuranceCertificate(referenceNumber: string): void {
    let marineInsuranceSearchShipmentParams: MarineInsuranceSearchShipmentModel;

    switch (this.companyPlacement) {
      case enumCompanyPlacement.ISF:
        marineInsuranceSearchShipmentParams = { isfReferenceNumber: referenceNumber };
        break;
      case enumCompanyPlacement.ENTRY_SUMMARY:
        marineInsuranceSearchShipmentParams = { entrySummaryReferenceNumber: referenceNumber };
        break;
      case enumCompanyPlacement.SHIPMENT:
        marineInsuranceSearchShipmentParams = { freightShipmentReferenceNumber: referenceNumber };
        break;
      default:
        return;
    }

    this.isDownloading = true;
    this.cdr.markForCheck();

    this.marineInsuranceFacade
      .getShipments({ ...marineInsuranceSearchShipmentParams })
      .pipe(
        switchMap((marineInsurance: MarineInsuranceModel) => this.marineInsuranceDocumentsRepository.getDocuments(marineInsurance.id)),
        switchMap((document: DocumentModel) => this.documentsRepository.getDownloadLink(document.id)),
        take(1),
        finalize(() => {
          this.isDownloading = false;
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (linkData: { downloadUrl: string }) => {
          this.insuranceCertificateLinkRef.nativeElement.href = linkData.downloadUrl;
          this.insuranceCertificateLinkRef.nativeElement.click();
        },
        // the shield is now a one-click action, so a silent failure would look like a dead button
        error: () => this.alertService.error('LABELS.SOMETHING_DONT_WORK_MESSAGE'),
      });
  }
}
